'use client';

import type { SelectedDate } from '@yanolja-rnd/nol-design-system';
import type { RoomCapacity } from '@/app/_components/common/date-capacity-select-bottom-sheet/capacity-select';
import type { UniversalSearchTabItemInfo, QuickFilterItem, SortInfoItem } from '@weberus/shared-types';
import type { UniversalSearchQuery } from '@/app/(pages)/results/type';
import type { DateCapacityOpenType } from '@/app/_components/common/date-capacity-select-bottom-sheet';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useHashModalAppRouter } from '@weberus/next-libs/feature/hash-modal-app-router';
import { generateHashedId, waitUntilClickTimeout } from '@weberus/utils';
import { MODAL__SWIPE_BACK__OPTION } from '@weberus/ui/utils/swipe-back-options';
import { Divider } from '@yanolja-rnd/nol-design-system';
import { parse, stringify } from 'qs';
import { format } from 'date-fns';
import { isNil, isUndefined, omitBy } from 'lodash-es';
import { setCookie } from 'cookies-next/client';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { cn } from '@/app/_shared/utils/tw-cn';
import { useScrollState } from '@/app/_shared/hooks/use-scroll-state';
import { hasItems } from '@/app/_shared/utils/has-items';
import { useUniversalSearchHeaderQuery } from '@/app/(pages)/results/api/queries';

import {
  globalFilterAtom,
  globalFilterParamsAtom,
  isTabScrollUpdatingAtom,
  originalGlobalFilterAtom,
  quickFilterAtom,
  searchFilterKeywordAtom,
  searchFilterSortAtom,
  sessionKeyAtom,
  sortInfoAtom,
} from '@/app/_shared/store/search-filter';
import {
  selectedVerticalTabInfoAtom,
  universalSearchRequestBodyAtom,
  verticalTabInfoAtom,
} from '@/app/_shared/store/universal-search';
import { globalCheckIOCapacityAtom, setGlobalCheckIOCapacityAtom } from '@/app/_shared/store/global-accommodation';
import { SortModal } from '@/app/_components/common/sort-modal';
import {
  capacityAdultAtom,
  capacityChildAgesAtom,
  capacityChildAtom,
  checkInDateAtom,
  checkOutDateAtom,
  localCheckIOCapacityAtom,
} from '@/app/_shared/store/local-accommodation';
import { VerticalTab } from '@/app/(pages)/results/(pages)/ui/search-header/vertical-tab';
import {
  DEFAULT_COOKIE_OPTIONS,
  CookieKey,
  MAX_STAY_DAYS_LOCAL,
  MAX_STAY_DAYS_GLOBAL,
  UniversalSearchCategory,
} from '@/app/(pages)/results/constants';
import { DateCapacitySelectBottomSheet } from '@/app/_components/common/date-capacity-select-bottom-sheet';
import { deviceAtom } from '@/app/_shared/store/device';
import { updateSessionLocalFilters } from '@/app/_shared/api';
import {
  getRestoredSearchFilterStorage,
  updateFilterStorage,
} from '@/app/_components/feature/plp-srp-filter/filter-storage';
import { checkAndRestoreScroll } from '@/app/_components/feature/plp-srp-filter/use-scroll-restoration';
import { transformQueryToGlobalFilter } from '@/app/_components/feature/global-filter-modal/utils/global-filter';
import { parseQueryObject } from '@/app/_shared/utils/query-string-utils';
import useScrollVisibility from '@/app/_shared/hooks/use-scroll-visibility';
import { useDynamicHeaderHeight } from '@/app/(pages)/results/hooks/use-dynamic-header-height';
import {
  createCheckInOutCapacityText,
  createGlobalCheckInOutCapacityText,
} from '@/app/_shared/utils/booking-text-utils';
import { ProductListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-web-txtpb';
import { AccommodationFilter } from '@/app/(pages)/results/(pages)/ui/search-header/accommodation-filter';
import { LeisureFilter } from '@/app/(pages)/results/(pages)/ui/search-header/leisure-filter';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { getCategoryQueryValueFromKey } from '@/app/(pages)/results/utils/get-category-query-value-from-key';
import {
  CategoryOverviewButton,
  CategoryOverviewMenu,
} from 'app/(pages)/results/(pages)/ui/search-header/category-overview';
import { GlobalFilter } from '@/app/(pages)/results/(pages)/ui/search-header/global-filter';
import { NavBar } from './nav-bar';

// 클라이언트 사이드 렌더링을 위한 전역 필터 모달 동적 임포트

export const SearchHeader = () => {
  // 날짜/인원 모달 상태 관리
  const [dateCapacityModalOpen, setDateCapacityModalOpen] = useHashModalAppRouter({
    id: `hash-${generateHashedId('date-capacity-modal')}`,
    swipeBackOption: MODAL__SWIPE_BACK__OPTION,
  });

  // 정렬 모달 상태 관리
  const [isSortFilterModalOpen, setIsSortFilterModalOpen] = useHashModalAppRouter({
    id: `hash-${generateHashedId('sort-filter')}`,
    swipeBackOption: MODAL__SWIPE_BACK__OPTION,
  });

  const [isGlobalFilterOpen, setIsGlobalFilterOpen] = useHashModalAppRouter({
    id: `hash-${generateHashedId('global-filter')}`,
    swipeBackOption: MODAL__SWIPE_BACK__OPTION,
  });

  // 버티컬탭 드랍메뉴 상태 관리
  const [categoryOverviewOpen, setCategoryOverviewOpen] = useState(false);
  const [isCategoryOverflowing, setIsCategoryOverflowing] = useState(false);

  // 스크롤 상태 관리 훅
  const hasScrolled = useScrollState();

  const { deviceType } = useAtomValue(deviceAtom);
  const keyword = useAtomValue(searchFilterKeywordAtom);
  const sessionKey = useAtomValue(sessionKeyAtom);

  // 캘린더
  const [checkInDate, setCheckInDate] = useAtom(checkInDateAtom);
  const [checkOutDate, setCheckOutDate] = useAtom(checkOutDateAtom);
  const [capacityAdult, setCapacityAdult] = useAtom(capacityAdultAtom);
  const [capacityChild, setCapacityChild] = useAtom(capacityChildAtom);
  const [capacityChildAges, setCapacityChildAges] = useAtom(capacityChildAgesAtom);
  const localCheckIOCapacity = useAtomValue(localCheckIOCapacityAtom);
  const globalCheckIOCapacity = useAtomValue(globalCheckIOCapacityAtom);
  const setGlobalCheckIOCapacity = useSetAtom(setGlobalCheckIOCapacityAtom);
  const [dateCapacityOpenType, setDateCapacityOpenType] = useState<DateCapacityOpenType | undefined>();

  // store setter
  const setOriginalGlobalFilter = useSetAtom(originalGlobalFilterAtom);
  const setGlobalFilter = useSetAtom(globalFilterAtom);
  const setListRequestBody = useSetAtom(universalSearchRequestBodyAtom);
  const setSort = useSetAtom(searchFilterSortAtom);
  const setFilters = useSetAtom(globalFilterParamsAtom);
  const setQuickFilter = useSetAtom(quickFilterAtom);

  const [sortInfo, setSortInfo] = useAtom(sortInfoAtom);
  const [verticalTabInfo, setVerticalTabInfo] = useAtom(verticalTabInfoAtom);
  const [selectedVerticalTabInfo, setSelectedVerticalTabInfo] = useAtom(selectedVerticalTabInfoAtom);

  const [isTabScrollUpdating, setIsTabScrollUpdating] = useAtom(isTabScrollUpdatingAtom);
  const isVisible = useScrollVisibility(isTabScrollUpdating);

  const { data: headerData } = useUniversalSearchHeaderQuery();
  const { fixedHeaderRef, flexibleHeaderRef, headerHeight, updateHeaderHeight } = useDynamicHeaderHeight();

  useEffect(() => {
    // 쿼리 스트링이 변경되어, 새로운 데이터를 받아올 때, 상태 업데이트
    if (!headerData) return;

    const selectedTab = headerData.tabInfo?.items?.filter((item) => item.selected)[0];
    setVerticalTabInfo(headerData.tabInfo?.items || []);
    setSelectedVerticalTabInfo(selectedTab || headerData.tabInfo?.items?.[0]);
    setQuickFilter({
      globalFilter: headerData?.globalFilter,
      quickFilter: headerData?.quickFilter,
      hasSelectedFilter: !!headerData?.hasSelectedFilter,
      includeFilterBtn: false,
    });
    setListRequestBody(headerData?.request || undefined);
    setSortInfo(headerData?.sortInfo);
    setSort(headerData?.request?.sort || 'RECOMMEND');
    setOriginalGlobalFilter(headerData?.globalFilter);
    setGlobalFilter(headerData?.globalFilter);

    // 탭 변경시 헤더 높이 업데이트
    setTimeout(() => updateHeaderHeight());
  }, [headerData]);

  const selectedIndex = useMemo(
    () => verticalTabInfo?.findIndex((item) => item.key === selectedVerticalTabInfo?.key),
    [selectedVerticalTabInfo],
  );

  const [rooms, selectedDate] = useMemo(() => {
    const key = selectedVerticalTabInfo?.key;
    if (key === 'LOCAL_ACCOMMODATION') {
      return [
        [
          {
            capacityAdult: capacityAdult ?? 2,
            capacityChild: capacityChild ?? 0,
            capacityChildAges: capacityChildAges ?? [],
          },
        ],
        checkInDate && checkOutDate ? [new Date(checkInDate), new Date(checkOutDate)] : [],
      ];
    }

    if (key === 'GLOBAL_ACCOMMODATION') {
      return [
        globalCheckIOCapacity.capacityRooms?.map((room) => ({
          capacityAdult: room.adult,
          capacityChild: room.child.length,
          capacityChildAges: room.child,
        })) ?? [],
        globalCheckIOCapacity.checkInDate && globalCheckIOCapacity.checkOutDate
          ? [new Date(globalCheckIOCapacity.checkInDate), new Date(globalCheckIOCapacity.checkOutDate)]
          : [],
      ];
    }
    return [[], [new Date(), new Date()]];
  }, [
    selectedVerticalTabInfo?.key,
    checkInDate,
    checkOutDate,
    capacityAdult,
    capacityChild,
    capacityChildAges,
    globalCheckIOCapacity,
  ]);

  const checkInOutCapacityText = useMemo(
    () =>
      createCheckInOutCapacityText(
        localCheckIOCapacity.checkInDate,
        localCheckIOCapacity.checkOutDate,
        localCheckIOCapacity.capacityAdult,
        localCheckIOCapacity.capacityChild,
      ),
    [localCheckIOCapacity],
  );

  const globalCheckInOutCapacityText = useMemo(
    () =>
      createGlobalCheckInOutCapacityText(
        globalCheckIOCapacity.checkInDate,
        globalCheckIOCapacity.checkOutDate,
        globalCheckIOCapacity.capacityRooms,
      ),
    [globalCheckIOCapacity],
  );

  const isFilterSelected = !!headerData?.hasSelectedFilter;
  const isSortSelected = !!headerData?.sortInfo?.items?.some((item) => item.selected && !item.isDefault);
  const hasQuickFilter = !!headerData?.quickFilter?.items && headerData?.quickFilter?.items?.length > 0;
  const hasGlobalFilter = !!headerData?.globalFilter?.items && headerData?.globalFilter?.items?.length > 0;
  const hasSortItems = !!headerData?.sortInfo?.items && headerData?.sortInfo?.items?.length > 0;
  const selectedTabKey = selectedVerticalTabInfo?.key;

  const handleImprChip = (item: QuickFilterItem & Partial<{ isGlobalFilter: boolean }>) => {
    if (item.isGlobalFilter) return; // FE 에서 하드코딩하는 globalFilter 는 로그 전송 안함

    clientLogProtobuf(ProductListPageLog.SearchButtonQuickFilter.impression, {
      selected: item.selected,
      logMeta: item.serverLogMeta,
    })();
  };

  const handleClickChip = (item: QuickFilterItem) => {
    if (item.type === 'OPEN_GLOBAL_FILTER') {
      clientLogProtobuf(ProductListPageLog.SearchButtonQuickFilter.click, {
        logMeta: {
          ...item.serverLogMeta,
          selected: !item.selected, // 필터를 선택하여서 적용되었을 때 selected 필드가 True로, 해제하였을 때 False로 남아야 함
        },
      })();

      setIsGlobalFilterOpen(true);
      return;
    }

    clientLogProtobuf(ProductListPageLog.SearchButtonQuickFilter.click, {
      logMeta: {
        ...item.serverLogMeta,
        selected: !item.selected, // 필터를 선택하여서 적용되었을 때 selected 필드가 True로, 해제하였을 때 False로 남아야 함
      },
    })();
  };

  const handleSortChange = (sortObj: SortInfoItem) => {
    clientLogProtobuf(ProductListPageLog.SearchButtonSetSortComplete.click, {
      logMeta: sortObj?.serverLogMeta,
    })();
  };

  const handleOpenGlobalFilter = () => {
    clientLogProtobuf(ProductListPageLog.SearchButtonFilterList.click, {
      logMeta: headerData?.globalFilter?.serverLogMeta,
    })();
    setIsGlobalFilterOpen(true);
  };

  const handleOpenSortModal = () => {
    clientLogProtobuf(ProductListPageLog.SearchButtonSetSort.click, { logMeta: sortInfo?.serverLogMeta })();
    setIsSortFilterModalOpen(true);
  };

  /**
   * 날짜/인원 변경 모달에서 적용 버튼 클릭 시 처리
   * 날짜와 인원 정보를 상태, 쿠키, URL 등에 업데이트하고 앱에도 동기화
   */
  const handleDateCapacityApply = async ({ date, rooms }: { date?: SelectedDate; rooms?: RoomCapacity[] }) => {
    // 해시 모달 닫기
    setDateCapacityModalOpen(false);
    setDateCapacityOpenType(undefined);

    // 모달을 닫을 때의 useHashModalAppRouter 내부 로직 (예. router.back())의 완료를 보장하기 위해 setTimeout 사용
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((resolve) => setTimeout(resolve, 100));

    // 인원/날짜 변경 시 URL의 pageKey 초기화 (캐시된 검색 결과 방지)
    const queryParams = parse(location.search.replace('?', ''));
    queryParams.pageKey = Date.now().toString();
    window.history.replaceState(null, '', `?${stringify(queryParams)}`);

    if (selectedVerticalTabInfo?.key === 'LOCAL_ACCOMMODATION') {
      handleLocalDateCapacityApply({ date, rooms });
    } else if (selectedVerticalTabInfo?.key === 'GLOBAL_ACCOMMODATION') {
      handleGlobalDateCapacityApply({ date, rooms });
    }
  };

  const handleLocalDateCapacityApply = async ({ date, rooms }: { date?: SelectedDate; rooms?: RoomCapacity[] }) => {
    // pageKey 초기화 후, 인원 날짜 변경 (순서 중요!)
    const checkinDate = date?.[0] ? format(date?.[0], 'yyyy-MM-dd') : undefined;
    const checkoutDate = date?.[1] ? format(date?.[1], 'yyyy-MM-dd') : undefined;
    const capacityAdult = rooms?.[0].capacityAdult ?? 2;
    const capacityChild = rooms?.[0].capacityChild ?? 0;
    const capacityChildAges = rooms?.[0].capacityChildAges ?? [];

    // jotai 상태 업데이트
    typeof checkinDate !== 'undefined' && setCheckInDate(checkinDate);
    typeof checkoutDate !== 'undefined' && setCheckOutDate(checkoutDate);
    typeof capacityAdult !== 'undefined' && setCapacityAdult(capacityAdult);
    typeof capacityChild !== 'undefined' && setCapacityChild(capacityChild);
    typeof capacityChildAges !== 'undefined' && setCapacityChildAges(capacityChildAges);

    // 스크롤 위치 초기화
    window.scrollTo(0, 0);

    // 세션 데이터 구성 (undefined 값 제외)
    const sessionData = omitBy(
      { checkinDate, checkoutDate, capacityAdult, capacityChild, capacityChildAges },
      isUndefined,
    );

    // 쿠키에 날짜 및 인원 정보 저장
    if (checkinDate && checkoutDate) {
      setCookie(CookieKey.CHECK_IN_OUT_DOMESTIC, JSON.stringify([checkinDate, checkoutDate]), DEFAULT_COOKIE_OPTIONS);
    }

    if (capacityAdult && capacityChild && capacityChildAges) {
      setCookie(
        CookieKey.CAPACITY_DOMESTIC,
        JSON.stringify([capacityAdult, capacityChild, capacityChildAges]),
        DEFAULT_COOKIE_OPTIONS,
      );
    }

    // 세션 데이터 업데이트 API 호출
    updateSessionLocalFilters({
      ...(sessionData || {}),
      prevCheckinDate: checkinDate,
      prevCheckoutDate: checkoutDate,
    });

    // 앱 환경에서 필터 정보 동기화
    if (yaNative.isEnabled) {
      yaNative.setFilter({
        type: 'domestic',
        hasToast: false,
        checkDate: {
          in: checkinDate || '',
          out: checkoutDate || '',
        },
        rooms: capacityAdult ? [{ adult: capacityAdult, childages: capacityChildAges }] : [],
      });
    }

    // 날짜 변경 로그 전송
    if (checkinDate && checkoutDate) {
      clientLogProtobuf(ProductListPageLog.SearchLabelChangeCheckInOutDate.bg, {
        logMeta: {
          checkInDate: checkinDate,
          checkOutDate: checkoutDate,
          roomPeople: [{ adult: capacityAdult, childages: capacityChildAges ?? [] }],
          ...selectedVerticalTabInfo?.serverLogMeta,
        },
      })();
    }
  };

  const handleGlobalDateCapacityApply = async ({ date, rooms }: { date?: SelectedDate; rooms?: RoomCapacity[] }) => {
    // pageKey 초기화 후, 인원 날짜 변경 (순서 중요!)
    const checkinDate = date?.[0] ? format(date?.[0], 'yyyy-MM-dd') : undefined;
    const checkoutDate = date?.[1] ? format(date?.[1], 'yyyy-MM-dd') : undefined;
    const capacityRooms =
      rooms?.map((room) => ({
        adult: room.capacityAdult || 2,
        child: room.capacityChildAges || 0,
      })) || [];

    setGlobalCheckIOCapacity({
      ...(checkinDate && checkoutDate ? { checkInDate: checkinDate, checkOutDate: checkoutDate } : {}),
      capacityRooms,
    });

    // 스크롤 위치 초기화
    window.scrollTo(0, 0);

    // 쿠키에 날짜 및 인원 정보 저장
    if (checkinDate && checkoutDate) {
      setCookie(CookieKey.CHECK_IN_OUT_GLOBAL, JSON.stringify([checkinDate, checkoutDate]), DEFAULT_COOKIE_OPTIONS);
    }

    setCookie(
      CookieKey.CAPACITY_GLOBAL,
      JSON.stringify(capacityRooms.map(({ adult, child }) => [adult, child.length, child])),
      DEFAULT_COOKIE_OPTIONS,
    );

    // 세션 데이터 업데이트 API 호출
    updateSessionLocalFilters({
      globalPlaceFilter: { checkinDate, checkoutDate, rooms: capacityRooms },
      globalPrevCheckinDate: checkinDate,
      globalPrevCheckoutDate: checkoutDate,
    });

    // 앱 환경에서 필터 정보 동기화
    if (yaNative.isEnabled) {
      yaNative.setFilter({
        type: 'overseas',
        hasToast: false,
        checkDate: {
          in: checkinDate || '',
          out: checkoutDate || '',
        },
        rooms: capacityRooms.map((room) => ({
          adult: room.adult,
          childages: room.child,
        })),
      });
    }

    clientLogProtobuf(ProductListPageLog.SearchLabelChangeCheckInOutDate.bg, {
      logMeta: {
        checkInDate: checkinDate,
        checkOutDate: checkoutDate,
        roomPeople: capacityRooms.map((room) => ({
          adult: room.adult,
          childages: room.child,
        })),
        ...selectedVerticalTabInfo?.serverLogMeta,
      },
    })();
  };

  const handleClickTab = async (item: UniversalSearchTabItemInfo) => {
    // 탭 클릭 로그 전송
    await waitUntilClickTimeout(
      ProductListPageLog.SearchTabBtnDisplayName.click({
        logMeta: { ...headerData?.serverLogMeta, ...item?.serverLogMeta },
      }),
    );

    //  놀티켓, 해외투어티켓, 공연/전시, 항공탭 선택시 페이지 전환
    if (
      item?.key === UniversalSearchCategory.NOL_TICKET ||
      item?.key === UniversalSearchCategory.GLOBAL_TNA ||
      item?.key === UniversalSearchCategory.FLIGHT ||
      item?.key === UniversalSearchCategory.ENTERTAINMENT
    ) {
      if (item?.action) {
        navigateTo({ app: item?.action.app, web: item?.action.web });
        return;
      }
    }

    // 현재 쿼리 문자열 파싱
    const currentQuery = parse(new URLSearchParams(window.location.search).toString());

    // 새로운 쿼리 문자열 생성 (필수 유지 쿼리만 보존)
    const newCategory = getCategoryQueryValueFromKey(item.key);
    const newQuery = omitBy(
      {
        keyword: currentQuery.keyword,
        category: newCategory,
        pageKey: currentQuery.pageKey,
      },
      (value) => isNil(value) || value === '',
    );

    // 세션 스토리지에서 저장된 필터 정보 복원
    const restoredSearchFilter = getRestoredSearchFilterStorage({ sessionKey });
    const restoredData = restoredSearchFilter?.[newQuery.pageKey as string]?.[newCategory] || {};

    // 현재 스크롤 위치 저장
    const currentScrollY = window.scrollY;

    updateFilterStorage({
      query: currentQuery,
      scrollY: currentScrollY,
      sessionKey,
      searchType: currentQuery?.category as UniversalSearchQuery['category'],
    });

    // 복원할 쿼리 생성 (저장된 쿼리가 있으면 사용, 없으면 신규 생성)
    const restoredQs = restoredData.qs ? parse(restoredData.qs) : newQuery;

    // URL 업데이트
    window.history.replaceState(null, '', `?${stringify(restoredQs)}`);

    // 상태 업데이트
    setIsTabScrollUpdating(true);

    setSelectedVerticalTabInfo(item);
    setQuickFilter({ globalFilter: null, quickFilter: null, hasSelectedFilter: false, includeFilterBtn: false });
    setSort(
      (restoredQs.sort ||
        (item?.key === UniversalSearchCategory.ENTERTAINMENT ? 'SOLD_COUNT_DESC' : 'RECOMMEND')) as string,
    );
    setFilters(transformQueryToGlobalFilter(parseQueryObject(restoredQs)));

    // 스크롤 위치 복원
    const { scrollY = 0 } = restoredData;
    checkAndRestoreScroll(scrollY, () => setIsTabScrollUpdating(false));
  };

  const handleImpressionTab = () => {
    // 탭이 화면에 노출되면 모든 탭에 대한 노출 로그 전송

    verticalTabInfo?.forEach((item) => {
      clientLogProtobuf(ProductListPageLog.SearchTabBtnDisplayName.impression, {
        logMeta: {
          ...headerData?.serverLogMeta,
          ...item?.serverLogMeta,
        },
      })();
    });
  };

  const handleClickCheckIODate = () => {
    clientLogProtobuf(ProductListPageLog.SearchButtonCheckInOutDateAndSetPeople.click, {
      logMeta: headerData?.serverLogMeta,
    })();
    setDateCapacityModalOpen(true);
    setDateCapacityOpenType('date');
  };

  const handleImpressionSort = () => {
    clientLogProtobuf(ProductListPageLog.SearchButtonSetSort.impression, {
      logMeta: headerData?.sortInfo?.serverLogMeta,
    })();
  };

  const handleClickMenu = async (item: UniversalSearchTabItemInfo) => {
    // clientId 699
    await waitUntilClickTimeout(ProductListPageLog.SearchTabBtnVerticalItem.click({ logMeta: item.serverLogMeta }));
    setCategoryOverviewOpen(!categoryOverviewOpen);
    handleClickTab(item);
  };

  const handleVerticalTabOverflow = (isOverflowing: boolean) => {
    setIsCategoryOverflowing(isOverflowing);
  };

  const handleImpressionCategoryOverviewButton = () => {
    clientLogProtobuf(ProductListPageLog.SearchTabBtnMore.impression, {
      logMeta: headerData?.serverLogMeta,
    })();
  };

  return (
    <>
      <header
        className={cn('fixed top-0 w-full bg-static-white z-middle pc:max-w-[unset]', {
          'pb-2 shadow-3': hasScrolled,
          'border-b-[1px] border-b-line-neutral-weak1 pb-2': !hasScrolled,
          '!border-b-[0px] !pb-0':
            !hasGlobalFilter &&
            !hasSortItems &&
            (selectedTabKey === 'LOCAL_LEISURE' || selectedTabKey === 'ENTERTAINMENT'),
        })}>
        <div ref={fixedHeaderRef} className="mx-auto max-w-legacy-pc-size">
          <div className="w-full bg-static-white pc:px-2 pc:py-6">
            <NavBar keyword={keyword} category={selectedTabKey} />
          </div>
        </div>
        <div ref={flexibleHeaderRef} className="relative z-[-1] w-full bg-static-white">
          <div style={{ marginTop: !isVisible ? -66 : 0 }} className="relative transition-all duration-300">
            <div className="flex flex-col items-center">
              {hasItems(verticalTabInfo) && (
                <div className={cn('w-full max-w-legacy-pc-size', deviceType !== 'etc' && 'pr-40')}>
                  <VerticalTab
                    tabInfo={verticalTabInfo}
                    selectedIndex={selectedIndex}
                    localCheckIOCapacity={localCheckIOCapacity}
                    globalCheckIOCapacity={globalCheckIOCapacity}
                    serverLogMeta={headerData?.serverLogMeta}
                    onClick={handleClickTab}
                    onImpression={handleImpressionTab}
                    onOverflowChange={(isOverflowing) => handleVerticalTabOverflow(isOverflowing)}
                  />
                  {isCategoryOverflowing && (
                    <CategoryOverviewButton
                      isOpen={categoryOverviewOpen}
                      onImpression={handleImpressionCategoryOverviewButton}
                      onButtonClick={() => {
                        // clientId 698
                        clientLogProtobuf(ProductListPageLog.SearchTabBtnMore.click, {
                          logMeta: headerData?.serverLogMeta,
                        })();
                        setCategoryOverviewOpen(!categoryOverviewOpen);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
            <Divider size="extra_small" />
          </div>
        </div>

        <CategoryOverviewMenu
          data={verticalTabInfo}
          headerHeight={headerHeight}
          isOpen={categoryOverviewOpen}
          onClickItem={(item: UniversalSearchTabItemInfo) => handleClickMenu(item)}
          onClose={() => setCategoryOverviewOpen(false)}
        />

        {(selectedTabKey === 'LOCAL_ACCOMMODATION' || selectedTabKey === 'GLOBAL_ACCOMMODATION') && (
          <AccommodationFilter
            tab={selectedTabKey}
            fixedAreaRef={fixedHeaderRef}
            flexibleAreaRef={flexibleHeaderRef}
            hasGlobalFilter={hasGlobalFilter}
            hasSortItems={hasSortItems}
            hasQuickFilter={hasQuickFilter}
            isFilterSelected={isFilterSelected}
            isSortSelected={isSortSelected}
            checkInOutCapacityText={checkInOutCapacityText}
            globalCheckInOutCapacityText={globalCheckInOutCapacityText}
            onClickGlobalFilter={handleOpenGlobalFilter}
            onClickSort={handleOpenSortModal}
            onClickChip={handleClickChip}
            onImpressionChip={handleImprChip}
            onClickCheckIODate={handleClickCheckIODate}
            onImpressionSort={handleImpressionSort}
          />
        )}
        {hasGlobalFilter &&
          hasSortItems &&
          (selectedTabKey === 'LOCAL_LEISURE' || selectedTabKey === 'ENTERTAINMENT') && (
            <LeisureFilter
              fixedAreaRef={fixedHeaderRef}
              hasQuickFilter={hasQuickFilter}
              isFilterSelected={isFilterSelected}
              isSortSelected={isSortSelected}
              onClickGlobalFilter={handleOpenGlobalFilter}
              onClickSort={handleOpenSortModal}
              onClickChip={handleClickChip}
              onImpressionChip={handleImprChip}
              onImpressionSort={handleImpressionSort}
            />
          )}
      </header>
      {/* 헤더 높이 유지용 div */}
      <div style={{ height: headerHeight + 2 }} />
      {/* 글로벌 필터 */}
      <GlobalFilter
        open={isGlobalFilterOpen}
        tabInfo={selectedVerticalTabInfo}
        serverLogMeta={headerData?.serverLogMeta}
        onCloseModal={() => setIsGlobalFilterOpen(false)}
      />
      {/* 정렬 모달 */}
      <SortModal
        isOpen={isSortFilterModalOpen}
        closeModal={() => setIsSortFilterModalOpen(false)}
        onSortChange={handleSortChange}
      />
      {/* 국내숙소 날짜/인원 선택 모달 */}
      <DateCapacitySelectBottomSheet
        deviceType={deviceType}
        isOpen={dateCapacityModalOpen}
        openType={dateCapacityOpenType}
        isGlobal={selectedTabKey === 'GLOBAL_ACCOMMODATION'}
        capacity={{ rooms }}
        date={{
          selectedDate: selectedDate as SelectedDate,
          maxDuration: selectedTabKey === 'GLOBAL_ACCOMMODATION' ? MAX_STAY_DAYS_GLOBAL : MAX_STAY_DAYS_LOCAL,
        }}
        onApply={handleDateCapacityApply}
        onClose={() => {
          setDateCapacityModalOpen(false);
          setDateCapacityOpenType(undefined);
        }}
        onDateModalOpen={() => {
          clientLogProtobuf(ProductListPageLog.SearchButtonCheckInOutDate.click, {
            containerDetail: 'bottomSheet',
            logMeta: headerData?.serverLogMeta,
          })();
        }}
        onCapacityModalOpen={() => {
          clientLogProtobuf(ProductListPageLog.SearchButtonSetPeople.click, {
            containerDetail: 'bottomSheet',
            logMeta: headerData?.serverLogMeta,
          })();
        }}
      />
    </>
  );
};
